import { call, ForkEffect, takeLatest } from 'redux-saga/effects';
import { SettingsCategories } from 'models/settings';
import { SetSettings } from 'store/actions/settings';
import i18n from 'i18n/i18n';
import { LocalStorage } from 'utils/enums/storage';
import localState from 'utils/local-storage';

import * as settingsActions from '../actions/settings';
import { LocaleDirectionMap } from 'models/i18n';
import { LanguagesCodes } from 'utils/enums/language';

// ======================= Workers (Effects) =======================

function* settingsUpdateSaga(action: SetSettings) {
  // Make sure correct language is set: 1st priority is device preference, fallback to site default or env var.
  // Note: we do this here because this action (SetSettings) is the common action to all events associated
  // with locale updates: Login, Auto-Login + Switch View (i.e. when user change site)
  const deviceLanguage = yield call(() => localState.load<string>(LocalStorage.Language));
  const normalizedDeviceLanguage = deviceLanguage?.split(':')[0];
  const { defaultLanguage } = action.payload.settings[SettingsCategories.Geolocation];
  const defaultNamespace = Array.isArray(i18n.options.defaultNS) ? i18n.options.defaultNS[0] : i18n.options.defaultNS || 'default';
  const baseLanguage = normalizedDeviceLanguage || defaultLanguage || import.meta.env.VITE_DEFAULT_LANAGUAGE || 'en';
  const nextLanguage = i18n.hasResourceBundle(baseLanguage, defaultNamespace) ? baseLanguage : 'en';

  if (normalizedDeviceLanguage && normalizedDeviceLanguage !== deviceLanguage) {
    yield call(() => localState.save(LocalStorage.Language, normalizedDeviceLanguage));
  }

  yield call([i18n, i18n.changeLanguage], nextLanguage);

  if (import.meta.env.DEV) {
    // Temporary debug logs - remove after confirming language switching works.
    console.debug('[i18n] Language changed (settings saga)', i18n.language);
    console.debug('[i18n] Has default namespace', i18n.hasResourceBundle(i18n.language, defaultNamespace));
  }

  const localeDirection = LocaleDirectionMap[nextLanguage as LanguagesCodes];

  document.documentElement.style.setProperty('--direction', localeDirection);
}

// ======================= Watchers =======================

export function* watchSettings(): IterableIterator<ForkEffect> {
  yield takeLatest(settingsActions.SettingsActions.SetSettings, settingsUpdateSaga);
}
